import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsNotificationTheme } from '@design-system/ds-notification/types/ds-notification-theme.type';

@Component({
  selector: 'ds-notification',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatMenuModule, DsButtonComponent],
  templateUrl: './ds-notification.component.html',
  styleUrls: ['./ds-notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsNotificationComponent {
  /**
   * List of options shown in snooze menu.
   */
  @Input() snoozeOptions: BaseDropdownInterface[] = [];

  /**
   * Controls the visual state of the notification.
   *
   * @default 'info'
   */
  @Input() theme: DsNotificationTheme = 'info';

  /**
   * Notification title displayed in the top row.
   */
  @Input() header = '';

  /**
   * Additional descriptive text displayed under the header.
   */
  @Input() content = '';

  /**
   * Controls whether the optional "Snooze" action is shown.
   *
   * @default false
   */
  @Input() hasSnooze = false;

  /**
   * Event emitted when "View" action is clicked.
   */
  @Output() viewClick: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Event emitted when a snooze option is selected.
   */
  @Output() snoozeSelect: EventEmitter<number> = new EventEmitter<number>();

  /**
   * Event emitted when "Dismiss" action is clicked.
   */
  @Output() dismissClick: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Returns all CSS classes required for the notification card.
   */
  get hostClasses(): string[] {
    return ['ds-notification--' + this.theme];
  }

  /**
   * Returns the icon name based on the selected notification theme.
   */
  get themeIcon(): string {
    switch (this.theme) {
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'cancel';
      case 'info':
      default:
        return 'info';
    }
  }

  /**
   * Emits selected snooze value.
   * @param value - selected snooze time in minutes
   */
  onSnoozeSelect(value: string): void {
    this.snoozeSelect.emit(+value);
  }
}
